/**
 * Should this build record Sentry Session Replay, and how? (CORE-979)
 *
 * ⛔ ПАУЗА (20.08.2026, решение владельца): запись выключена ВЕЗДЕ — она съедала
 * квоту Sentry. Выключено здесь, а не в настройках Sentry, чтобы причина была
 * видна в коде рядом с правилом, которое она отменяет.
 *
 * Обратно включается одной правкой — `PAUSED_FOR_QUOTA = false`. Всё остальное
 * (маскирование, запрет на нативе, гейт по окружению) не тронуто и продолжает
 * действовать, как только запись вернут.
 *
 * Если квоту надо придержать, но запись хочется сохранить, есть второй рычаг,
 * которого здесь нет: лимиты и spike protection в самом Sentry — они режут
 * расход без выкладки. Этот файл про «не писать вовсе».
 *
 * Replay is a web-only, non-prod tool here:
 *  - WEB only. On native, @sentry/react-native attaches its *mobile* replay
 *    integration automatically the moment `replaysSessionSampleRate` or
 *    `replaysOnErrorSampleRate` is a number (see integrations/default.js). We
 *    don't want native recording without a separate product decision, so a
 *    disabled config must omit those keys ENTIRELY — returning them as 0 would
 *    still trip the auto-attach on native.
 *  - NON-PROD only. Prod (agapella.io) puts real users on camera; the owner
 *    decides prod separately. Gating by `environment` inside init — rather than
 *    a manual rollout — means the next release can't flip prod on by accident.
 *    `environment` values are case-sensitive (CORE-931), so we compare against
 *    the exact SentryEnvironment union produced by resolveSentryEnvironment.
 *
 * Masking (mask all text + all inputs, so login/password never land in the
 * recording) is configured on the integration itself in App.tsx; this helper
 * only decides on/off + the sample rates so it stays a pure, testable unit.
 */
import type { SentryEnvironment } from './sentryEnvironment';

export type SentryReplayConfig = {
  enabled: boolean;
  /** Present ONLY when enabled — see the native auto-attach note above. */
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
};

/**
 * Запись приостановлена целиком. Отдельная константа, а не голый `return`:
 * так видно, что это выключатель, а не отмена самой возможности, и обратный
 * ход стоит одной правки.
 */
export const PAUSED_FOR_QUOTA = true;

export function resolveSentryReplayConfig(
  platformOS: string,
  environment: SentryEnvironment,
): SentryReplayConfig {
  // ⛔ Пауза по расходу квоты. `PAUSED_FOR_QUOTA = false` возвращает запись.
  // Стоит первой намеренно: пока она здесь, ни одно окружение и ни одна
  // платформа записи не ведут, и разбираться в правилах ниже не нужно.
  if (PAUSED_FOR_QUOTA) return { enabled: false };

  // Native is out of scope for CORE-979 — no rates, so the SDK doesn't
  // auto-enable mobile replay.
  if (platformOS !== 'web') return { enabled: false };
  // Prod is owner-gated; everything else (dev / staging / preview) records.
  if (environment === 'production') return { enabled: false };
  return {
    enabled: true,
    // Every session on the non-prod stands (CORE-983). The 10% sample looked
    // frugal until the first real use: the master who could not go live on
    // 20.08 was simply not among the sampled 10%, and the failure raised no
    // Sentry error either (the studio bailed on a guard, not an exception),
    // so `replaysOnErrorSampleRate` didn't catch it. Dev and staging carry QA
    // traffic only — recording all of it is what makes the tool answer
    // «покажи, что видел тестер» instead of «не повезло».
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  };
}
